// src/pages/Accounts/IncomeEntry.js
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  Flex,
  message,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchIncomeCode,
  fetchIncomes,
  saveIncome,
  fetchHeadAccs,
} from "./incomeThunk";
import {
  fetchTagGroups,
  fetchTagGroupFields,
  fetchEmployees,
  fetchAccountsByType,
} from "../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  getAccessType,
  getDateTimeFromSelectedDate,
} from "../../lib/functions";
import {
  setEntryDate,
  setIsEditIncome,
  setSelectedIncome,
  setRefreshList,
  setIncomeCode,
} from "./incomeSlice";
import AddNewModal from "../../components/DropDown/AddNewModal";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import { selectAuthInfo } from "../Auth/authSelectors";
import useMigrationDate from "../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const IncomeEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const authInfo = useSelector(selectAuthInfo);

  const [rows, setRows] = useState([]);
  const [selectedPayIntoAcc, setselectedPayIntoAcc] = useState(null);
  // const [selectedEmployeeLocal, setSelectedEmployeeLocal] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const {
    entryDate,
    incomeCode,
    headAccs,
    accountBalances,
    selectedIncome,
    isEditIncome,
  } = useSelector((state) => state.incomes);
  const { accountsByType, employees } = useSelector((s) => s.lookups);
  let [access_type, access_type_set] = useState("");
  const [tagModal, setTagModal] = useState(false);

  useEffect(() => {
    const access = getAccessType("income_entry");
    access_type_set(access);
  }, []);

  // initial load
  useEffect(() => {
    if (!id) dispatch(fetchIncomeCode());
    dispatch(fetchHeadAccs());
    dispatch(fetchEmployees());
    dispatch(fetchAccountsByType(["cash_in_hand", "bank_account"]));
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(
          groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap())
        )
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      })
      .catch(() => { });
    setRows([
      {
        key: Date.now() + Math.random(),
        from_acc_id: 0,
        acc_name: "",
        amount: 0,
      },
    ]);
  }, [dispatch]);

  // if direct edit route accessed
  useEffect(() => {
    if (id && !selectedIncome) {
      dispatch(fetchIncomes())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.inc_id == id);
          console.log(list, "list");

          if (found) {
            dispatch(setSelectedIncome(found));
            dispatch(setIsEditIncome(true));
          }
        })
        .catch(() => { });
    }
  }, [id, selectedIncome, dispatch]);

  // fill form when edit selected
  useEffect(() => {
    if (isEditIncome && selectedIncome) {
      dispatch(setIncomeCode(selectedIncome.inc_code));
     const foundAcc = accountsByType.find(
        (a) =>a.acc_id == selectedIncome.from_acc_id
      );
      //  setselectedPayIntoAcc(foundAcc);
      // setselectedPayIntoAcc({
      //   display_text: selectedIncome.acc_name,
      //   acc_name: selectedIncome.acc_name,
      // });
      // Income Code, Date, Narration
      form.setFieldsValue({
        inc_date: dayjs(selectedIncome.created_date),
        description: selectedIncome.narration,
        into_acc: selectedIncome.into_acc_id,
      });

      // Tags
      const tagValues = selectedIncome.tag_field_id
        ? [
          {
            display_text: selectedIncome.tag_name, // needs API join or lookup
            value: selectedIncome.tag_field_id,
          },
        ]
        : [];
      setSelectedTags(selectedIncome.tag_field_id);
      form.setFieldsValue({ tags: selectedIncome.tag_field_id });

      // Income heads (rows)
      setRows(
        (selectedIncome.details || []).map((d) => ({
          key: d.inc_d_id || Date.now() + Math.random(),
          inc_head: {
            display_text: d.acc_name,
            value: d.from_acc_id,
          },
          from_acc_id: d.from_acc_id,
          acc_name: d.acc_name,
          amount: d.inc_amount || 0,
        }))
      );

      // Employee
      // if (selectedIncome.employee_id) {
      //   const foundEmp = employees.find(
      //     (e) => Number(e.employee_id) === Number(selectedIncome.employee_id)
      //   );
      //   if (foundEmp) {
      //     const empValue = {
      //       sale_id: foundEmp.employee_id, // 🔑 must match option.key
      //       display_text: `${foundEmp.employee_name} - ${foundEmp.employee_code}`,
      //       raw: foundEmp,
      //     };
      //     setSelectedEmployeeLocal(foundEmp); // optional
      //   }
      // }

      // Pay From Account
      if (selectedIncome.into_acc_id && accountsByType.length > 0) {
        const foundAcc = accountsByType.find(
          (a) => a.acc_id == selectedIncome.into_acc_id
        );
        if (foundAcc) {
          const accValue = {
            sale_id: foundAcc.acc_id, // 🔑 must match option.key
            acc_id: foundAcc.acc_id, // 🔑 must match option.key
            display_text: foundAcc.acc_name,
            raw: foundAcc,
          };
          setselectedPayIntoAcc(accValue);
          form.setFieldsValue({
            into_acc: accValue,
          });
          // setselectedPayIntoAcc(foundAcc); // optional
        }
      }

      // Entry Date in slice
      dispatch(setEntryDate(dayjs(selectedIncome.created_date).toISOString()));
    }
  }, [isEditIncome, selectedIncome, employees, accountsByType, form, dispatch]);

  // add/remove rows
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        from_acc_id: 0,
        acc_name: "",
        amount: 0,
      },
    ]);
  };
  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedIncome(null));
    dispatch(setIsEditIncome(false));
    navigate("/accounts/income");
  };

  // Save
  const onFinish = async (values) => {
    // convert selected/entry Date to utc date + time
    let finalEntryDate = entryDate;
   if (!dayjs(entryDate).isSame(dayjs(), "day")) {
      finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ
    } else {
      finalEntryDate = entryDate; // Use now in TZ
    }

    try {
      const totalAmount = rows.reduce(
        (sum, r) => sum + Number(r.amount || 0),
        0
      );

      if (!selectedPayIntoAcc?.acc_id) {
        modal.error({ title: "Select a Received Into Account before saving." });
        return;
      }
      if (!entryDate) {
        modal.error({ title: "Entry Date is required!" });
        return;
      }
      if (rows.length === 0 || totalAmount === 0) {
        modal.error({ title: "Income rows cannot be empty." });
        return;
      }

      for (let r of rows) {
        if (!r.acc_name) {
          modal.error({ title: "Please select Income Head before saving." });
          return;
        }
        if (r.amount == null || r.amount <= 0) {
          modal.error({ title: "Please enter a valid Amount before saving." });
          return;
        }
      }

      const payloadIncome = {
        inc_id: isEditIncome ? selectedIncome.inc_id : 0,
        action: isEditIncome ? "update" : "create",
        inc_code: incomeCode,
        // created_date: values.inc_date.toISOString(), // already date picker value
        created_date: formatToUtc(finalEntryDate),
        into_acc_id: selectedPayIntoAcc.acc_id,
        // employee_id: selectedEmployeeLocal?.employee_id || 0,
        // pay_to: selectedEmployeeLocal
        //   ? `${selectedEmployeeLocal.employee_name} - ${selectedEmployeeLocal.employee_code}`
        //   : "",
        inc_total: totalAmount,
        narration: values.description || "",
      };

      if (selectedTags.length > 0) {
        payloadIncome.tag_field_id = selectedTags;
      } else {
        payloadIncome.tag_field_id = undefined;
      }

      const payload = {
        income: payloadIncome,
        incomeDetail: rows.map((r) => ({
          from_acc_id: r.from_acc_id,
          acc_name: r.acc_name,
          inc_amount: Number(r.amount) || 0,
        })),
      };

      const res = await dispatch(saveIncome(payload)).unwrap();
      modal.success({ title: res.msg || "Income saved successfully" });

      // Reset after save
      dispatch(setSelectedIncome(null));
      dispatch(setIsEditIncome(false));
      dispatch(setRefreshList(true));

      form.resetFields();
      setRows([]);
      navigate("/accounts/income");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save income" });
    }
  };

  // columns for income rows
  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Income Head",
      dataIndex: "acc_name",
      render: (val, record, index) => (
        <div>
          <SearchListAutoComplete
            entityType="accounts"
            style={{ width: "100%" }}
            optionsList={(headAccs || []).map((acc) => ({
              sale_id: acc.acc_id,
              display_text: acc.acc_name,
              raw: acc,
            }))}
            onSelect={(obj) => {
              const newRows = [...rows];
              newRows[index].from_acc_id = obj?.raw?.acc_id || 0;
              newRows[index].acc_name = obj?.raw?.acc_name || "";
              setRows(newRows);
            }}
            value={
              record.acc_name
                ? {
                  value: record.acc_name,
                  label: record.acc_name,
                }
                : null
            }
            placeholder="Income Head"
            showAddNew={false}
          />
        </div>
      ),
    },
    {
      title: "Income Amount",
      dataIndex: "amount",
      width: screens.md ? undefined : 110,
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          data-testid={`income-amount-input`}
          value={record.amount}
          step={1} // allow decimal steps
          precision={2} // max 2 decimal places
          style={{ width: "100%" }}
          placeholder="0.00"
          onClick={() => {
            const newRows = [...rows];

            if (newRows[index].amount === 0) {
              newRows[index].amount = "";
              setRows(newRows);
            }
          }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].amount = val || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
         <Button type="link" danger onClick={() => handleRemoveRow(record.key)} icon={<DeleteOutlined />}/>   
      ),
    },
  ];

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const { disabledDate } = useMigrationDate();
  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Income Entry</Title>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1400px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ inc_date: dayjs(entryDate) }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Income Code */}
            <Form.Item label="Income Code">
              <Input value={incomeCode} disabled />
            </Form.Item>

            {/* Income Date */}
            <Form.Item
              name="inc_date"
              label="Income Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                disabledDate={disabledDate}
              />
            </Form.Item>
          </Flex>
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Pay Into Account */}
            <Form.Item
              name="into_acc"
              label="Receipt Into Acc"
            // rules={[{ required: true, message: "Please Select Account" }]}
            >
              <SearchListAutoComplete
                entityType="accounts"
                style={{ width: "100%" }}
                optionsList={(accountsByType || []).map((acc) => ({
                  sale_id: acc.acc_id,
                  display_text: acc.acc_name,
                  raw: acc,
                }))}
                onSelect={(obj) => {
                  setselectedPayIntoAcc(obj?.raw);
                }}
                value={selectedPayIntoAcc}
                placeholder="Cash / Bank"
                showAddNew={false}
              />
            </Form.Item>

            {/* Employee */}
            {/* <Form.Item
        name="employee_id" 
        label="Employee"        
        >
        <SearchListAutoComplete
            entityType="employee"
            style={{ width: "100%" }}
            placeholder="Employee"
            optionsList={(employees || []).map((emp) => ({
            sale_id: emp.employee_id, 
            display_text: `${emp.employee_name} - ${emp.employee_code}`, 
            raw: emp,
            }))}
            onSelect={(obj) => {                      
            setSelectedEmployeeLocal(obj?.raw);
            }}
            showAddNew={false}
        />
        </Form.Item> */}

            {/* Tags */}
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({
                  value: item.id,
                  label: item.combinedField,
                }))}
                onChange={(ids) => setSelectedTags(ids)}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
        >
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          style={{ marginTop: 16, marginBottom: 24 }}
          scroll={{ x: "max-content" }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>
                Total Income Amount: {Number(totalAmount).toFixed(2)}
              </strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} data-testid="income-description-input" />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="btn-save-income"
              >
                Save Income
              </Button>
            </>
          )}
        </Space>
      </Form>
      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t.raw.field_name.toLowerCase() === createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              return;
            }

            // 🔥 UPDATE OPTIONS FIRST
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists
                ? prev
                : [
                  ...prev,
                  {
                    ...match.raw,
                    combinedField: match.display_text,
                    id: match.id,
                  },
                ];
            });

            const current = form.getFieldValue("tags") || [];

            if (current.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              return;
            }

            // ✅ NOW auto-select
            form.setFieldsValue({
              tags: [...current, match.id],
            });
            setSelectedTags([...current, match.id]);

            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default IncomeEntry;
